import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import NavItemPage from './pages/NavItemPage';
import ProductPage from './pages/ProductPage';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Impersonate from './pages/Impersonate';
import CategoryPage from './pages/CategoryPage';
import AllCategoriesPage from './pages/CategoriesPage';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import AllProducts from './pages/AllProducts';
import DocumentMeta from './components/DocumentMeta';
import FacebookPixel from './components/FacebookPixel';
import ScrollToTop from './components/ScrollToTop';
import { HelmetProvider } from 'react-helmet-async';
import './styles/global.css';
import { Provider } from 'react-redux';
import { store } from './store';
import { Toaster } from 'react-hot-toast';
import OrderConfirmation from './pages/OrderConfirmation';
import { StoreProvider } from './store/StoreContext';
import { TenantProvider, useTenant } from './store/TenantContext';
import ThemeProvider from './components/ThemeProvider';
import TenantNotFound from './pages/TenantNotFound';
import Landing from './pages/Landing';
import styles from './App.module.css';

// Korpa je lazy zbog Stripe-a: @stripe/react-stripe-js i Payment Element
// trebaju samo na /cart, a staticki import ih je vukao u main bundle.
const CartPage = lazy(() => import('./pages/CartPage'));

// Admin + super-admin routes are lazy — they pull heavy editors (Quill,
// xlsx, etc.) that the storefront has no use for. Keeps storefront bundle
// ~250 KiB lighter.
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const SuperAdminLayout = lazy(() => import('./layouts/SuperAdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ProductsPage = lazy(() => import('./pages/admin/ProductsPage'));
const ProductsDashboard = lazy(() => import('./pages/admin/products/ProductsDashboard'));
const OrdersDashboard = lazy(() => import('./pages/admin/orders/OrdersDashboard'));
const MarketingDashboard = lazy(() => import('./pages/admin/marketing/MarketingDashboard'));
const CategoriesPage = lazy(() => import('./pages/admin/CategoriesPage'));
const BlogDashboard = lazy(() => import('./pages/admin/blog/BlogDashboard'));
const StoreSettingsHub = lazy(() => import('./pages/admin/settings/StoreSettingsHub'));
const Billing = lazy(() => import('./pages/admin/Billing'));
const ReviewsManagement = lazy(() => import('./pages/admin/ReviewsManagement'));
const AddCollection = lazy(() => import('./components/admin/AddCollection'));
const AddBlogPost = lazy(() => import('./components/admin/blog/AddBlogPost'));
const BlogPostList = lazy(() => import('./components/admin/blog/BlogPostList'));
const EditBlogPost = lazy(() => import('./components/admin/blog/EditBlogPost'));
const OrderList = lazy(() => import('./components/admin/orders/OrderList'));
const OrderDetails = lazy(() => import('./components/admin/orders/OrderDetails'));
const CustomerList = lazy(() => import('./components/admin/customers/CustomerList'));
const CustomerDetails = lazy(() => import('./components/admin/customers/CustomerDetails'));
const CouponList = lazy(() => import('./components/admin/marketing/CouponList'));
const NewsletterList = lazy(() => import('./components/admin/marketing/NewsletterList'));
const SalesAnalytics = lazy(() => import('./components/admin/analytics/SalesAnalytics'));
const VisitorAnalytics = lazy(() => import('./components/admin/analytics/VisitorAnalytics'));
const ConversionAnalytics = lazy(() => import('./components/admin/analytics/ConversionAnalytics'));
const InventoryManagement = lazy(() => import('./components/admin/inventory/InventoryManagement'));
const SupplierManagement = lazy(() => import('./components/admin/suppliers/SupplierManagement'));
const UserManagement = lazy(() => import('./components/admin/users/UserManagement'));
const CurrencySettings = lazy(() => import('./components/admin/settings/CurrencySettings'));
const TaxSettings = lazy(() => import('./components/admin/settings/TaxSettings'));
const ShippingSettings = lazy(() => import('./components/admin/settings/ShippingSettings'));
const AddBundle = lazy(() => import('./components/admin/bundles/AddBundle').then(m => ({ default: m.AddBundle })));
const BundleList = lazy(() => import('./components/admin/bundles/BundleList').then(m => ({ default: m.BundleList })));
const EditBundle = lazy(() => import('./components/admin/bundles/EditBundle'));
const VendorList = lazy(() => import('./pages/admin/VendorList'));
const VendorForm = lazy(() => import('./pages/admin/VendorForm'));
const ModuleList = lazy(() => import('./components/admin/modules/ModuleList'));
const ModuleForm = lazy(() => import('./components/admin/modules/ModuleForm'));
const TenantsList = lazy(() => import('./pages/super-admin/TenantsList'));
const NewTenant = lazy(() => import('./pages/super-admin/NewTenant'));
const UsersList = lazy(() => import('./pages/super-admin/UsersList'));
const SuperAdminDashboard = lazy(() => import('./pages/super-admin/Dashboard'));
const EditUser = lazy(() => import('./pages/super-admin/EditUser'));
const EmailLogs = lazy(() => import('./pages/super-admin/EmailLogs'));
const PlansList = lazy(() => import('./pages/super-admin/PlansList'));
const PlanForm = lazy(() => import('./pages/super-admin/PlanForm'));

const RouteFallback = () => (
  <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', minHeight: '70vh' }}>Učitavanje...</div>
);

function App() {
  return (
    <HelmetProvider>
    <TenantProvider>
    <StoreProvider>
      <Provider store={store}>
        <Router>
          <AppShell />
        </Router>
      </Provider>
    </StoreProvider>
    </TenantProvider>
    </HelmetProvider>
  );
}

function AppShell() {
  const location = useLocation();
  const { loading, notFound, slug } = useTenant();
  const isAdmin =
    location.pathname.startsWith('/admin') || location.pathname.startsWith('/super-admin');
  const isAuthPage = ['/login', '/register', '/verify-email'].includes(location.pathname);
  // Storefront chrome (Header + Footer) only appears on tenant-scoped
  // pages — never on root marketing pages, admin panels, or auth flows.
  const showChrome = !isAdmin && !isAuthPage && slug !== null;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', minHeight: '100vh' }}>Učitavanje...</div>;
  // Subdomain present but no matching tenant — show a friendly 404 for the
  // whole app. Super-admin lives on root domain, so this won't hide it.
  if (notFound) return <TenantNotFound />;

  return (
          <ThemeProvider>
          <div className={styles.app}>
            <ScrollToTop />
            <DocumentMeta />
            {/* Pixel prati samo storefront — admin klikovi nisu saobraćaj kupaca. */}
            {!isAdmin && <FacebookPixel />}
            <Toaster position="top-right" />
            {showChrome && <Header />}
            <main className={styles.content}>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={slug ? <Home /> : <Landing />} />
                <Route path="/admin/*" element={<AdminLayout />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="products/*" element={<ProductsPage />} />
                  <Route path="categories/*" element={<CategoriesPage />} />
                  {/* Single hub with tabs; old routes redirect to the right tab. */}
                  <Route path="settings/store" element={<StoreSettingsHub />} />
                  <Route path="settings/payment-tax" element={<Navigate to="/admin/settings/store#payment-tax" replace />} />
                  <Route path="settings/footer" element={<Navigate to="/admin/settings/store#footer" replace />} />
                  <Route path="settings/header" element={<Navigate to="/admin/settings/store#header" replace />} />
                  <Route path="settings/seo" element={<Navigate to="/admin/settings/store#seo" replace />} />
                  <Route path="settings/integrations" element={<Navigate to="/admin/settings/store#integrations" replace />} />
                  <Route path="settings/homepage" element={<Navigate to="/admin/settings/store#homepage" replace />} />
                  <Route path="settings/currency" element={<CurrencySettings />} />
                  <Route path="settings/tax" element={<TaxSettings />} />
                  <Route path="settings/shipping" element={<ShippingSettings />} />
                  <Route path="settings/modules" element={<ModuleList />} />
                  <Route path="settings/modules/add" element={<ModuleForm />} />
                  <Route path="settings/modules/edit/:id" element={<ModuleForm isEdit />} />
                  <Route path="orders" element={<OrderList />} />
                  <Route path="customers" element={<CustomerList />} />
                  <Route path="marketing/coupons" element={<CouponList />} />
                  <Route path="marketing/newsletter" element={<NewsletterList />} />
                  <Route path="analytics/sales" element={<SalesAnalytics />} />
                  <Route path="analytics/visitors" element={<VisitorAnalytics />} />
                  <Route path="analytics/conversions" element={<ConversionAnalytics />} />
                  <Route path="inventory" element={<InventoryManagement />} />
                  <Route path="suppliers" element={<SupplierManagement />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="reviews" element={<ReviewsManagement />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="bundles">
                    <Route index element={<BundleList />} />
                    <Route path="add" element={<AddBundle />} />
                    <Route path="edit/:id" element={<EditBundle />} />
                  </Route>
                  <Route path="vendors">
                    <Route index element={<VendorList />} />
                    <Route path="add" element={<VendorForm />} />
                    <Route path="edit/:id" element={<VendorForm isEdit />} />
                  </Route>
                </Route>
                <Route path="/super-admin" element={<SuperAdminLayout />}>
                  <Route index element={<SuperAdminDashboard />} />
                  <Route path="users" element={<UsersList />} />
                  <Route path="users/:id/edit" element={<EditUser />} />
                  <Route path="tenants" element={<TenantsList />} />
                  <Route path="tenants/new" element={<NewTenant />} />
                  <Route path="email-logs" element={<EmailLogs />} />
                  <Route path="plans" element={<PlansList />} />
                  <Route path="plans/new" element={<PlanForm />} />
                  <Route path="plans/:id/edit" element={<PlanForm isEdit />} />
                </Route>
                <Route path="/p/:slug" element={<ProductPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/__impersonate" element={<Impersonate />} />
                <Route path="/categories" element={<AllCategoriesPage />} />
                <Route path="/category/:category" element={<CategoryPage />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/products" element={<AllProducts />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/order-confirmation" element={<OrderConfirmation />} />
                <Route path="/admin/add-collection" element={<AddCollection />} />
                <Route path="/admin/blog" element={<BlogDashboard />} />
                <Route path="/admin/blog/posts" element={<BlogPostList />} />
                <Route path="/admin/blog/add" element={<AddBlogPost />} />
                <Route path="/admin/blog/edit/:id" element={<EditBlogPost />} />
                <Route path="/admin/products-dashboard" element={<ProductsDashboard />} />
                <Route path="/admin/orders-dashboard" element={<OrdersDashboard />} />
                <Route path="/admin/marketing-dashboard" element={<MarketingDashboard />} />
                <Route path="/admin/orders/view/:id" element={<OrderDetails />} />
                <Route path="/admin/customers/:id" element={<CustomerDetails />} />
                {/* Catch-all for header nav items with curated products
                    (e.g. /collections, /trending). Falls back to a 404-style
                    message if the path doesn't match a configured nav item. */}
                <Route path="/:slug" element={<NavItemPage />} />
              </Routes>
              </Suspense>
            </main>
            {showChrome && <Footer />}
          </div>
          </ThemeProvider>
  );
}

export default App;
